/* ── Support Buff Aura ────────────────────────────────────────────
 * Green radiating ring spawned at each ally drone position within 18u
 * of the support source. Lifetime ~1s per ring; the whole effect is a
 * group of N rings, one per ally.
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec2, vec3, vec4, float, length, smoothstep } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { vec3DistanceSq } from '../../../shared/utils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 1.0;
const RING_MAX_RADIUS = 3.5;
const COLOR_HEX = 0x6affad;
const ALLY_RADIUS_LIMIT = 18;

interface RingHandle {
  mesh: THREE.Mesh;
  geometry: THREE.RingGeometry;
  material: THREE.MeshBasicNodeMaterial;
  uProgress: ReturnType<typeof uniform>;
}

export class SupportBuffAura extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly group: THREE.Group;
  private readonly rings: RingHandle[] = [];
  private readonly liveResolver?: LivePositionResolver;
  private readonly initialSourcePos = new THREE.Vector3();

  /**
   * @param scene Three.js scene to add to
   * @param source Origin (support drone position)
   * @param allies Ally drone positions; only those within 18u of source spawn rings
   */
  constructor(
    scene: THREE.Scene,
    source: Vec3Like,
    allies: Vec3Like[],
    options?: { liveResolver?: LivePositionResolver },
  ) {
    super(scene, LIFE_SECONDS);
    this.group = new THREE.Group();
    this.liveResolver = options?.liveResolver;
    this.initialSourcePos.set(source.x, source.y, source.z);

    // Always include source position. Filter allies by 18u radius.
    const positions: Vec3Like[] = [source];
    const radiusSq = ALLY_RADIUS_LIMIT * ALLY_RADIUS_LIMIT;
    for (const ally of allies) {
      if (vec3DistanceSq(ally, source) <= radiusSq) {
        positions.push(ally);
      }
    }

    for (const pos of positions) {
      this.rings.push(this.createRing(pos));
    }

    this.scene.add(this.group);
  }

  private createRing(position: Vec3Like): RingHandle {
    const uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const geometry = new THREE.RingGeometry(0.6, 1.0, 48, 1);
    const material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const colorNode = Fn(() => {
      const u = uv();
      const radial = length(u.sub(vec2(0.5, 0.5))).mul(2.0);
      const edge = smoothstep(float(0.5), float(1.0), radial);
      const fade = float(uProgress).oneMinus();
      const rgb = vec3(uColor).add(vec3(0.15, 0.3, 0.2).mul(edge));
      const alpha = edge.mul(fade).mul(0.65);
      return vec4(rgb, alpha);
    })();
    material.colorNode = colorNode;
    setEmissiveNode(material, colorNode);

    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(position.x, position.y + 0.4, position.z);
    mesh.scale.setScalar(0.001);
    this.group.add(mesh);
    return { mesh, geometry, material, uProgress };
  }

  protected tick(t: number): void {
    // Translate the whole group so all rings ride along with the caster
    // (relative geometry is preserved).
    this.applyLiveDelta(this.group, this.liveResolver, this.initialSourcePos);
    const eased = 1 - (1 - t) * (1 - t);
    const scale = eased * RING_MAX_RADIUS;
    for (const ring of this.rings) {
      ring.mesh.scale.setScalar(scale);
      ring.uProgress.value = t;
    }
  }

  protected disposeResources(): void {
    this.scene.remove(this.group);
    for (const ring of this.rings) {
      ring.geometry.dispose();
      ring.material.dispose();
    }
    this.rings.length = 0;
  }
}
